import type { AppRole } from '@/types';

/**
 * Rank-based role helpers mirroring the backend's
 * `CrmIntelligence.Core.Constants.Roles` (`RankOf` / `MeetsMinimum`).
 *
 * Four global roles form a strict rank hierarchy — each tier includes
 * everything the tiers below it can do:
 *   Viewer (0) < Analyst (1) < Editor (2) < Admin (3)
 *
 * Auth0 is the sole source of truth for role assignment. Roles arrive on the
 * namespaced roles claim (`https://iris-northamerica.com/roles`). Admin is
 * the only unscoped role and is assignable ONLY via the Auth0 portal — never
 * from this application's UI or API.
 */
export const ROLES = {
  Viewer: 'crm:viewer',
  Analyst: 'crm:analyst',
  Editor: 'crm:editor',
  Admin: 'crm:admin',
} as const satisfies Record<string, AppRole>;

/** Rank order, lowest to highest. Used for "at least this tier" checks. */
export const ROLE_RANKS: readonly AppRole[] = [
  ROLES.Viewer,
  ROLES.Analyst,
  ROLES.Editor,
  ROLES.Admin,
];

/** Friendly display labels for each role claim value. */
export const ROLE_LABELS: Record<AppRole, string> = {
  'crm:viewer': 'Viewer',
  'crm:analyst': 'Analyst',
  'crm:editor': 'Editor',
  'crm:admin': 'Admin',
};

/** Bootstrap badge colors per rank (Viewer → Admin). */
export const ROLE_BADGE_COLORS: Record<AppRole, string> = {
  'crm:viewer': 'secondary',
  'crm:analyst': 'info',
  'crm:editor': 'primary',
  'crm:admin': 'danger',
};

/** True when the value is one of the four recognized role claim values. */
export function isAppRole(value: unknown): value is AppRole {
  return typeof value === 'string' && (ROLE_RANKS as readonly string[]).includes(value);
}

/** Returns the rank of `role`, or -1 if unrecognized/absent. */
export function rankOf(role: string | null | undefined): number {
  return role == null ? -1 : (ROLE_RANKS as readonly string[]).indexOf(role);
}

/** True if `role` is recognized and ranks at or above `minimum`. */
export function meetsMinimum(role: string | null | undefined, minimum: AppRole): boolean {
  return rankOf(role) >= rankOf(minimum);
}

/**
 * Highest-ranked recognized role among the token's roles claim values.
 * Roles are global per user so normally there is exactly one, but this
 * tolerates several being present and takes the strongest (mirrors the
 * backend's `GetHighestRole`). Returns null when no recognized role exists.
 */
export function highestRole(roles: readonly unknown[]): AppRole | null {
  let best: AppRole | null = null;
  for (const candidate of roles) {
    if (isAppRole(candidate) && rankOf(candidate) > rankOf(best)) {
      best = candidate;
    }
  }
  return best;
}
